/**
 * SOUND-775 — owner JOB TITLE, internal-only.
 *
 * Kurv's Stratus requires a `title` for every owner and we had none: `ownerType`
 * (owner / signer) is a ROLE, not a title, so 100% of applications were blocked
 * on it. CPO (plan §12.3): a new `owners.title`, editable per owner on the
 * Registration tab by INTERNAL users only, and NOT in the merchant wizard.
 *
 * That is why it is injected here rather than declared in `applicationSchema`:
 * the schema drives the merchant wizard too, so adding it there would put it in
 * front of merchants. This module adds the field to the RECORD-VIEW render model
 * (utilities/recordSchema.js) for internal callers and leaves everyone else's
 * model untouched — clients and partners do not see it at all.
 *
 * The save path is the existing per-row owner editor (RecordFieldRow →
 * PUT /applications/:id/owners/:ownerId with a flat `{ title }` body), so no new
 * component and no new request shape.
 */

import { displayValue, isEmptyValue } from "./displayValue";

/**
 * The Stratus owner-title vocabulary, in Stratus's order (index + 1 is its
 * numeric id).
 *
 * SOURCE OF TRUTH: sound-api `config/ownerTitles.mjs`, copied from Stratus's
 * published OpenAPI spec. This is a mirror of that file — change it there first,
 * then here. The API refuses anything off this list with a 400
 * (`details[0].field === "title"`), so drift shows up as a refused save, not as
 * bad data.
 */
export const OWNER_TITLES = [
  "President",
  "Vice President",
  "Secretary",
  "Sole Proprietor",
  "Treasurer",
  "Partner",
  "Member",
  "CFO",
  "Owner",
  "CEO",
  "Recorder",
];

export const OWNER_TITLE_FIELD = "title";
export const OWNER_TITLE_GROUP = "internalOwnerFields";

/**
 * Options for the select. A stored value outside the list is kept as an option,
 * so opening the editor never silently blanks a title someone already set.
 */
export function ownerTitleOptions(current) {
  if (isEmptyValue(current) || OWNER_TITLES.includes(current)) return OWNER_TITLES;
  return [...OWNER_TITLES, current];
}

/** A record-view field descriptor, same shape recordSchema.buildFields emits. */
export function ownerTitleDescriptor(owner) {
  const value = owner ? owner[OWNER_TITLE_FIELD] : undefined;
  return {
    name: OWNER_TITLE_FIELD,
    label: "Job Title (Kurv)",
    type: "select",
    gridCols: 2,
    tooltip: "Internal only. Required by Kurv for every owner.",
    required: false,
    options: ownerTitleOptions(value),
    otherField: null,
    otherValue: undefined,
    group: OWNER_TITLE_GROUP,
    unit: null,
    min: null,
    max: null,
    value,
    display: displayValue(value),
    isEmpty: isEmptyValue(value),
  };
}

/**
 * Add the internal-only title field to every owner block.
 *
 * Pure: returns a new sections array; the input is not mutated. A no-op for a
 * non-internal caller, for sections other than owners, and if the schema ever
 * starts declaring `title` itself (so the field can never render twice).
 *
 * @param {Array}  sections     output of buildRecordSections
 * @param {object} application  the stored document (for each owner's value)
 * @param {{ isInternal?: boolean }} ctx
 */
export function withInternalOwnerFields(sections, application, { isInternal = false } = {}) {
  if (!isInternal || !Array.isArray(sections)) return sections;
  return sections.map((section) => {
    if (section.key !== "owners" || !section.repeatable) return section;
    const owners = Array.isArray(application?.[section.arrayField || "owners"])
      ? application[section.arrayField || "owners"]
      : [];
    return {
      ...section,
      blocks: section.blocks.map((block) => {
        const declared = block.groups.some((g) =>
          g.fields.some((f) => f.name === OWNER_TITLE_FIELD)
        );
        if (declared) return block;
        // By id first — the index is only a fallback for a pre-SOUND-649 row.
        const owner =
          (block.rowId && owners.find((o) => o && o.ownerId === block.rowId)) ||
          owners[block.index];
        return {
          ...block,
          groups: [
            ...block.groups,
            {
              key: OWNER_TITLE_GROUP,
              title: "Internal — Kurv submission",
              variant: null,
              fields: [ownerTitleDescriptor(owner)],
            },
          ],
        };
      }),
    };
  });
}
